<template>
  <div
    v-if="visible"
    ref="containerRef"
    class="csc-panel"
    :class="{ 'csc-panel--dragging': isDragging }"
    :style="panelStyle"
  >
    <div class="csc-header" @mousedown="startDrag">
      <div class="csc-header__left">
        <Icon icon="mdi:phone-outline" width="18" height="18" />
        <span>Call Script</span>
      </div>
      <div class="csc-header__right">
        <button type="button" class="csc-header__btn" title="Copy all" @click.stop="handleCopyAll">
          <Icon :icon="copyIcon" width="16" height="16" />
        </button>
        <button
          type="button"
          class="csc-header__btn csc-header__btn--close"
          title="Close"
          @click.stop="emit('close')"
        >
          <Icon icon="mdi:close" width="18" height="18" />
        </button>
      </div>
    </div>
    <div class="csc-body">
      <div
        v-for="(step, idx) in callSteps"
        :key="idx"
        class="csc-step"
        :style="{ borderLeftColor: step.color }"
      >
        <span class="csc-step__label" :style="{ color: step.color }">{{ step.label }}</span>
        <p class="csc-step__content">{{ step.content }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from "vue";
import { Icon } from "@iconify/vue";
import type { CallScriptStep } from "@/types/outreach-cue-cards";

interface Props {
  callSteps: CallScriptStep[];
  visible: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  close: [];
}>();

const containerRef = ref<HTMLElement | null>(null);
const isDragging = ref(false);
const posX = ref(0);
const posY = ref(0);
const hasBeenPositioned = ref(false);
const dragOffsetX = ref(0);
const dragOffsetY = ref(0);

const copyLabel = ref("Copy All");
let copyTimeout: ReturnType<typeof setTimeout> | null = null;

const copyIcon = computed(() => (copyLabel.value === "Copied!" ? "mdi:check" : "mdi:content-copy"));

const panelStyle = computed(() => {
  if (!hasBeenPositioned.value) return {};
  return {
    left: `${posX.value}px`,
    top: `${posY.value}px`,
    transform: "none",
  };
});

// Reset position each time the panel opens
watch(
  () => props.visible,
  (val) => {
    if (val) {
      hasBeenPositioned.value = false;
    }
  }
);

function startDrag(event: MouseEvent) {
  if ((event.target as HTMLElement).closest(".csc-header__btn")) return;

  isDragging.value = true;

  const el = containerRef.value;
  if (el) {
    const rect = el.getBoundingClientRect();
    if (!hasBeenPositioned.value) {
      posX.value = rect.left;
      posY.value = rect.top;
      hasBeenPositioned.value = true;
    }
    dragOffsetX.value = event.clientX - posX.value;
    dragOffsetY.value = event.clientY - posY.value;
  }

  document.addEventListener("mousemove", onDrag);
  document.addEventListener("mouseup", stopDrag);
  event.preventDefault();
}

function onDrag(event: MouseEvent) {
  if (!isDragging.value) return;
  posX.value = Math.max(0, Math.min(event.clientX - dragOffsetX.value, window.innerWidth - 420));
  posY.value = Math.max(0, Math.min(event.clientY - dragOffsetY.value, window.innerHeight - 100));
}

function stopDrag() {
  isDragging.value = false;
  document.removeEventListener("mousemove", onDrag);
  document.removeEventListener("mouseup", stopDrag);
}

async function handleCopyAll() {
  const text = props.callSteps.map((s) => `[${s.label}]\n${s.content}`).join("\n\n");
  try {
    await navigator.clipboard.writeText(text);
    copyLabel.value = "Copied!";
    if (copyTimeout) clearTimeout(copyTimeout);
    copyTimeout = setTimeout(() => {
      copyLabel.value = "Copy All";
    }, 2000);
  } catch {
    copyLabel.value = "Failed";
    if (copyTimeout) clearTimeout(copyTimeout);
    copyTimeout = setTimeout(() => {
      copyLabel.value = "Copy All";
    }, 2000);
  }
}

onBeforeUnmount(() => {
  document.removeEventListener("mousemove", onDrag);
  document.removeEventListener("mouseup", stopDrag);
  if (copyTimeout) clearTimeout(copyTimeout);
});
</script>

<style scoped>
.csc-panel {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 420px;
  max-width: calc(100vw - 32px);
  max-height: 80vh;
  background: #ffffff;
  border-radius: 12px;
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.15),
    0 4px 16px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 1060;
}

.csc-panel--dragging {
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.2);
  transition: none;
  user-select: none;
}

.csc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: linear-gradient(90deg, #2d3bff 0%, #4f63ff 100%);
  color: #ffffff;
  flex-shrink: 0;
  cursor: grab;
  border-radius: 12px 12px 0 0;
}

.csc-header:active {
  cursor: grabbing;
}

.csc-header__left {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  font-weight: 600;
}

.csc-header__right {
  display: flex;
  align-items: center;
  gap: 4px;
}

.csc-header__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.15);
  color: #ffffff;
  cursor: pointer;
  transition: background 0.15s;
}

.csc-header__btn:hover {
  background: rgba(255, 255, 255, 0.25);
}

.csc-header__btn--close:hover {
  background: rgba(255, 255, 255, 0.25);
}

.csc-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.csc-step {
  border-left: 3px solid #e5e7eb;
  padding: 8px 12px;
  background: #fafafa;
  border-radius: 0 8px 8px 0;
}

.csc-step__label {
  display: block;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}

.csc-step__content {
  font-size: 0.8125rem;
  line-height: 1.5;
  color: #374151;
  margin: 0;
  white-space: pre-wrap;
}
</style>
